/**
 * Reducer + helpers for the annotation flow's local form state.
 *
 * Owns the step cursor, per-dimension selection, "none apply" toggle,
 * and the visited-step set. Pure — no data fetching, no navigation,
 * no side effects.
 */
import { useReducer } from 'react';

import type {
  DimensionLabels,
  GoLabAnnotationPass,
  GoLabDatasetRecordStatus,
} from '../types';
import type { TaxonomyDimension } from '../../types/taxonomy';


export interface DimensionState {
  selected: string[];
  noneApply: boolean;
}

export interface FlowState {
  currentStep: number;
  perDimension: Record<string, DimensionState>;
  visited: Set<number>;
  confidenceNote: string;
}

export type FlowAction =
  | { type: 'toggleLabel'; dimKey: string; labelId: string }
  | { type: 'toggleNoneApply'; dimKey: string }
  | { type: 'goToStep'; index: number }
  | { type: 'setConfidenceNote'; value: string };


export const initialFlowState: FlowState = {
  currentStep: 0,
  perDimension: {},
  visited: new Set([0]),
  confidenceNote: '',
};


export const flowReducer = (state: FlowState, action: FlowAction): FlowState => {
  switch (action.type) {
    case 'toggleLabel': {
      const existing = state.perDimension[action.dimKey] ?? {
        selected: [],
        noneApply: false,
      };
      const has = existing.selected.includes(action.labelId);
      const nextSelected = has
        ? existing.selected.filter((id) => id !== action.labelId)
        : [...existing.selected, action.labelId];
      // Selecting a label always flips "none apply" off — selection wins.
      return {
        ...state,
        perDimension: {
          ...state.perDimension,
          [action.dimKey]: { selected: nextSelected, noneApply: false },
        },
      };
    }
    case 'toggleNoneApply': {
      const existing = state.perDimension[action.dimKey] ?? {
        selected: [],
        noneApply: false,
      };
      const nextNone = !existing.noneApply;
      return {
        ...state,
        perDimension: {
          ...state.perDimension,
          [action.dimKey]: {
            // Toggling ON clears selections; toggling OFF leaves the
            // (already-cleared) array. Restoring previous selection
            // on toggle-off is a forgiveness polish — out of scope.
            selected: nextNone ? [] : existing.selected,
            noneApply: nextNone,
          },
        },
      };
    }
    case 'goToStep': {
      const next = new Set(state.visited);
      next.add(action.index);
      return { ...state, currentStep: action.index, visited: next };
    }
    case 'setConfidenceNote':
      return { ...state, confidenceNote: action.value };
    default:
      return state;
  }
};


export const useAnnotationFlow = () =>
  useReducer(flowReducer, initialFlowState);


/** Map record status → which pass the current user should submit. */
export const inferAnnotationPass = (
  status: GoLabDatasetRecordStatus,
): GoLabAnnotationPass | null => {
  if (status === 'pending' || status === 'assigned_first') return 'first';
  if (status === 'assigned_second' || status === 'annotated_first') return 'second';
  return null;
};


/** Convert the reducer's per-dim map to the wire shape (DimensionLabels). */
export const serialiseLabels = (
  dimensions: TaxonomyDimension[],
  perDimension: FlowState['perDimension'],
): DimensionLabels => {
  const out: DimensionLabels = {};
  for (const dim of dimensions) {
    const ds = perDimension[dim.id];
    // `noneApply` and "no selection" both serialise as an empty array —
    // matches the agreement-check contract.
    out[dim.id] = ds && !ds.noneApply ? ds.selected : [];
  }
  return out;
};
